import { Link, useNavigate } from 'react-router-dom';
import { PageHelmet } from '@/components/seo/PageHelmet';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import {
  Sparkles,
  Calendar,
  Clock,
  Star,
  Search,
  MapPin,
  Scissors,
  Sparkle,
  Heart,
  Hand,
  Eye,
  Smile,
  Users,
  Wand2,
  CheckCircle,
  Receipt,
  Package,
  BarChart3,
  Webhook,
  Briefcase,
  Check,
  ArrowRight
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { FeaturedProviders } from '@/components/home/FeaturedProviders';
import { TestimonialsSection } from '@/components/home/TestimonialsSection';
import { JobBoardSection } from '@/components/home/JobBoardSection';

const TICKER_SERVICES = [
  'Corte de pelo', 'Balayage', 'Manicura gel', 'Facial hidratante',
  'Depilación láser', 'Extensiones de pestañas', 'Masaje relajante', 'Tratamiento capilar'
];

const MARQUEE_ITEMS = [
  'Corte & Peinado', 'Coloración', 'Manicura', 'Pedicura',
  'Extensiones', 'Tratamiento Facial', 'Depilación', 'Masaje',
  'Maquillaje', 'Barbería', 'Pestañas', 'Cejas',
];

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');
  const [tickerIndex, setTickerIndex] = useState(0);

  // Ticker rotation every 2s
  useEffect(() => {
    const interval = setInterval(() => {
      setTickerIndex((prev) => (prev + 1) % TICKER_SERVICES.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (location) params.set('province', location);
    navigate(`/providers?${params.toString()}`);
  };

  // Funcionalidades data
  const funcionalidades = [
    {
      icon: Calendar,
      title: t('home.feat_bookings'),
      description: t('home.feat_bookingsDesc')
    },
    {
      icon: Users,
      title: t('home.feat_clients'),
      description: t('home.feat_clientsDesc')
    },
    {
      icon: Receipt,
      title: t('home.feat_invoicing'),
      description: t('home.feat_invoicingDesc')
    },
    {
      icon: Package,
      title: t('home.feat_inventory'),
      description: t('home.feat_inventoryDesc')
    },
    {
      icon: BarChart3,
      title: t('home.feat_analytics'),
      description: t('home.feat_analyticsDesc')
    },
    {
      icon: Clock,
      title: t('home.feat_timeClock'),
      description: t('home.feat_timeClockDesc')
    },
    {
      icon: Webhook,
      title: t('home.feat_integrations'),
      description: t('home.feat_integrationsDesc')
    },
    {
      icon: Briefcase,
      title: t('home.feat_jobs'),
      description: t('home.feat_jobsDesc')
    }
  ];

  // Pricing plans
  const pricingPlans = [
    {
      name: t('home.plan_basic'),
      price: '49.99',
      description: t('home.plan_basicDesc'),
      features: [
        t('home.plan_basicF1'),
        t('home.plan_basicF2'),
        t('home.plan_basicF3'),
        t('home.plan_basicF4'),
        t('home.plan_basicF5'),
        t('home.plan_basicF6')
      ],
      highlighted: false
    },
    {
      name: t('home.plan_pro'),
      price: '99.99',
      description: t('home.plan_proDesc'),
      features: [
        t('home.plan_proF1'),
        t('home.plan_proF2'),
        t('home.plan_proF3'),
        t('home.plan_proF4'),
        t('home.plan_proF5'),
        t('home.plan_proF6'),
        t('home.plan_proF7'),
        t('home.plan_proF8')
      ],
      highlighted: true
    },
    {
      name: t('home.plan_premium'),
      price: '149.99',
      description: t('home.plan_premiumDesc'),
      features: [
        t('home.plan_premiumF1'),
        t('home.plan_premiumF2'),
        t('home.plan_premiumF3'),
        t('home.plan_premiumF4'),
        t('home.plan_premiumF5'),
        t('home.plan_premiumF6'),
        t('home.plan_premiumF7'),
        t('home.plan_premiumF8')
      ],
      highlighted: false
    }
  ];

  const categories = [
    { name: t('home.cat_haircuts'), slug: 'peluqueria', icon: Scissors },
    { name: t('home.cat_manicure'), slug: 'manicura', icon: Hand },
    { name: t('home.cat_makeup'), slug: 'maquillaje', icon: Sparkle },
    { name: t('home.cat_waxing'), slug: 'depilacion', icon: Heart },
    { name: t('home.cat_lashes'), slug: 'facial', icon: Eye },
    { name: t('home.cat_facials'), slug: 'facial', icon: Smile },
    { name: t('home.cat_massage'), slug: 'masajes', icon: Users },
    { name: t('home.cat_treatments'), slug: 'estetica', icon: Wand2 },
  ];

  return (
    <>
      <PageHelmet
        title={t('home.seoTitle')}
        description={t('home.seoDescription')}
      />

      {/* ═══════════════ HERO SPLIT ═══════════════ */}
      <section className="bg-cream min-h-screen flex items-center relative overflow-hidden">
        <div className="container mx-auto max-w-7xl px-4 py-16 lg:py-0">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left col */}
            <div className="space-y-8" style={{ animationDelay: '0.1s' }}>
              {/* Label tag */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(184,134,11,0.06)] border border-[#B8860B]/20">
                <Sparkles className="h-4 w-4 text-[#B8860B]" />
                <span className="font-body text-sm font-medium text-[#B8860B]">
                  {t('home.heroBadge')}
                </span>
              </div>

              <h1 className="font-heading text-4xl md:text-5xl lg:text-[3.4rem] leading-tight text-[#2B2520]">
                {t('home.heroTitle1')}{' '}
                <em className="text-[#B8860B] italic">{t('home.heroTitle2')}</em>
              </h1>

              <p className="font-body text-lg text-[#8C7E6F] max-w-lg">
                {t('home.heroSubtitle')}
              </p>

              {/* Search bar */}
              <form onSubmit={handleSearch}>
                <Card className="p-3 shadow-ref-card bg-white border-0 rounded-2xl">
                  <div className="flex flex-col md:flex-row items-center gap-3">
                    <div className="flex-1 relative w-full">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8C7E6F]" />
                      <Input
                        placeholder={t('home.searchPlaceholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-transparent border-0 font-body text-[#2B2520] placeholder:text-[#8C7E6F] focus-visible:ring-0"
                      />
                    </div>
                    <div className="hidden md:block w-px h-8 bg-[#B8860B]/10" />
                    <div className="flex-1 relative w-full">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8C7E6F]" />
                      <Input
                        placeholder={t('home.locationPlaceholder')}
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        className="pl-10 bg-transparent border-0 font-body text-[#2B2520] placeholder:text-[#8C7E6F] focus-visible:ring-0"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="bg-[#B8860B] hover:bg-[#D4A526] text-white rounded-xl px-6 font-body font-semibold md:w-auto w-full"
                    >
                      <Search className="mr-2 h-4 w-4" />
                      {t('home.searchButton')}
                    </Button>
                  </div>
                </Card>
              </form>

              {/* Ticker */}
              <div className="flex items-center gap-2 font-body text-sm text-[#8C7E6F]">
                <span>Popular:</span>
                <span
                  key={tickerIndex}
                  className="text-[#B8860B] font-semibold"
                  style={{ animation: 'ticker 2s ease-in-out' }}
                >
                  {TICKER_SERVICES[tickerIndex]}
                </span>
              </div>

              {/* Inline stats */}
              <div className="flex gap-8 pt-2">
                <div>
                  <span className="font-heading text-2xl font-bold text-[#2B2520]">150+</span>
                  <span className="font-body text-sm text-[#8C7E6F] ml-1">Servicios</span>
                </div>
                <div>
                  <span className="font-heading text-2xl font-bold text-[#2B2520]">7.5K+</span>
                  <span className="font-body text-sm text-[#8C7E6F] ml-1">Clientas</span>
                </div>
              </div>
            </div>

            {/* Right col — hero image */}
            <div className="relative hidden lg:block">
              <div className="rounded-3xl overflow-hidden shadow-ref-card" style={{ aspectRatio: '4/5' }}>
                <img
                  src="https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800&q=80"
                  alt="Salón de belleza"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating card — booking */}
              <div
                className="absolute bottom-8 -left-6 bg-white rounded-2xl p-4 shadow-ref-float animate-float flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-full bg-[#B8860B]/10 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-[#B8860B]" />
                </div>
                <div>
                  <p className="font-body text-sm font-semibold text-[#2B2520]">Reserva confirmada</p>
                  <p className="font-body text-xs text-[#8C7E6F]">Ritual Piel de Alma · 16:00</p>
                </div>
              </div>
              {/* Floating badge — rating */}
              <div
                className="absolute top-8 -right-4 bg-white rounded-2xl px-4 py-3 shadow-ref-float animate-float"
                style={{ animationDelay: '1.5s' }}
              >
                <div className="flex items-center gap-2">
                  <Star className="h-5 w-5 fill-[#B8860B] text-[#B8860B]" />
                  <span className="font-body font-bold text-[#2B2520]">5.0</span>
                  <span className="font-body text-xs text-[#8C7E6F]">(128)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ MARQUEE ═══════════════ */}
      <section className="py-6 border-y border-[rgba(184,134,11,0.06)] bg-cream overflow-hidden">
        <div className="marquee-track">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span
              key={i}
              className="font-heading text-[clamp(1.75rem,4vw,3.25rem)] text-[rgba(184,134,11,0.1)] hover:text-[#B8860B] transition-colors duration-300 whitespace-nowrap mx-8 cursor-default select-none"
            >
              {item}
            </span>
          ))}
        </div>
      </section>

      {/* Featured Providers Section */}
      <FeaturedProviders />

      {/* ═══════════════ CATEGORIES (pills) ═══════════════ */}
      <section className="py-20 px-4 bg-cream">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-12">
            <h2 className="font-heading text-3xl md:text-4xl text-[#2B2520]">
              {t('home.categoriesTitle')}
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <Link
                key={category.name}
                to={`/providers?category=${category.slug}`}
              >
                <div className="flex items-center gap-3 px-5 py-3 rounded-full border border-[#B8860B]/15 bg-white hover:border-[#B8860B] hover:-translate-y-0.5 hover:shadow-ref-float transition-all duration-300 cursor-pointer group">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[rgba(184,134,11,0.06)] to-[rgba(184,134,11,0.12)] flex items-center justify-center group-hover:from-[#B8860B] group-hover:to-[#D4A526]">
                    <category.icon className="h-4 w-4 text-[#B8860B] group-hover:text-white transition-colors" />
                  </div>
                  <span className="font-body font-medium text-[#2B2520] text-sm">{category.name}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ HOW IT WORKS ═══════════════ */}
      <section className="py-20 px-4" style={{ background: 'linear-gradient(180deg, #F9F5EE, #FFFDF9)' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-12">
            <h2 className="font-heading text-3xl md:text-4xl text-[#2B2520]">
              {t('home.howItWorksTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Search, badge: t('home.step1Badge'), title: t('home.step1Title'), desc: t('home.step1Desc'), num: '01' },
              { icon: Calendar, badge: t('home.step2Badge'), title: t('home.step2Title'), desc: t('home.step2Desc'), num: '02' },
              { icon: CheckCircle, badge: t('home.step3Badge'), title: t('home.step3Title'), desc: t('home.step3Desc'), num: '03' },
            ].map((step) => (
              <div key={step.num} className="bg-white rounded-[20px] p-8 shadow-ref-card text-center space-y-4">
                <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-[#B8860B] to-[#D4A526] flex items-center justify-center">
                  <step.icon className="h-7 w-7 text-white" />
                </div>
                <span className="font-body text-xs font-bold tracking-widest text-[#B8860B] uppercase">
                  PASO {step.num}
                </span>
                <h3 className="font-heading text-xl text-[#2B2520]">{step.title}</h3>
                <p className="font-body text-[#8C7E6F] text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ FEATURES ACCORDION ═══════════════ */}
      <section id="funcionalidades" className="py-20 px-4 bg-cream scroll-mt-16">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-[0.42fr_0.58fr] gap-12 items-start">
            {/* Left — sticky */}
            <div className="lg:sticky lg:top-24 space-y-6">
              <Badge className="bg-[#B8860B]/10 text-[#B8860B] border-[#B8860B]/30 hover:bg-[#B8860B]/20 font-body">
                <Sparkles className="h-3 w-3 mr-1" />
                {t('home.featuredBadge')}
              </Badge>
              <h2 className="font-heading text-3xl md:text-4xl text-[#2B2520]">
                {t('home.featuredTitle')}
              </h2>
              <p className="font-body text-[#8C7E6F] leading-relaxed">
                {t('home.featuredSubtitle')}
              </p>
              <Link to="/register">
                <Button className="bg-[#B8860B] hover:bg-[#D4A526] text-white rounded-xl font-body font-semibold mt-4">
                  {t('home.startFreeTrial')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            {/* Right — accordion rows */}
            <div className="space-y-2">
              {funcionalidades.map((feature, index) => (
                <div
                  key={index}
                  className="feat-row rounded-2xl px-5 py-4 flex items-start gap-4 border border-transparent hover:border-[#B8860B]/10 cursor-default"
                >
                  <div className="w-11 h-11 rounded-xl bg-[rgba(184,134,11,0.06)] flex items-center justify-center flex-shrink-0 group-hover:bg-gradient-to-br group-hover:from-[#B8860B] group-hover:to-[#D4A526]">
                    <feature.icon className="h-5 w-5 text-[#B8860B]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h3 className="font-body font-semibold text-[#2B2520]">{feature.title}</h3>
                      <span className="feat-num font-body text-xs text-[#8C7E6F]">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <div className="feat-desc font-body text-sm text-[#8C7E6F] mt-1">
                      {feature.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* ═══════════════ WHY US (3 cards) ═══════════════ */}
      <section className="py-20 px-4 bg-cream">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-12">
            <h2 className="font-heading text-3xl md:text-4xl text-[#2B2520]">
              {t('home.whyTitle')}
            </h2>
            <p className="font-body text-[#8C7E6F] text-lg">
              {t('home.whySubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Calendar, title: t('home.easyBookingTitle'), desc: t('home.easyBookingDesc') },
              { icon: Clock, title: t('home.realTimeTitle'), desc: t('home.realTimeDesc') },
              { icon: Star, title: t('home.verifiedTitle'), desc: t('home.verifiedDesc') },
            ].map((card, i) => (
              <div key={i} className="bg-white rounded-[20px] p-8 shadow-ref-card hover:shadow-lg hover:-translate-y-1 hover:border-[#B8860B]/20 border border-transparent transition-all duration-300">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#B8860B] to-[#D4A526] flex items-center justify-center mb-5">
                  <card.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-heading text-xl text-[#2B2520] mb-3">{card.title}</h3>
                <p className="font-body text-[#8C7E6F] text-sm leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Board Section */}
      <JobBoardSection />

      {/* ═══════════════ PRICING ═══════════════ */}
      <section id="precios" className="py-20 px-4 bg-cream scroll-mt-16">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-12">
            <Badge className="bg-[#B8860B]/10 text-[#B8860B] border-[#B8860B]/30 hover:bg-[#B8860B]/20 font-body">
              <Star className="h-3 w-3 mr-1" />
              {t('home.pricingBadge')}
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl text-[#2B2520]">
              {t('home.pricingTitle')}
            </h2>
            <p className="font-body text-[#8C7E6F] max-w-2xl mx-auto">
              {t('home.pricingSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`relative rounded-[20px] p-7 transition-all duration-300 ${
                  plan.highlighted
                    ? 'border-2 border-[#B8860B] shadow-xl scale-105 bg-white ring-2 ring-[#B8860B]/20'
                    : 'border border-[#B8860B]/10 bg-white hover:border-[#B8860B]/30 hover:shadow-lg'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#B8860B] text-white border-0 shadow-lg font-body">
                      <Star className="h-3 w-3 mr-1" />
                      {t('home.mostPopular')}
                    </Badge>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="font-heading text-xl text-[#2B2520] mb-1">{plan.name}</h3>
                  <p className="font-body text-sm text-[#8C7E6F] mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-heading text-4xl font-bold text-[#B8860B]">{'\u20AC'}{plan.price}</span>
                    <span className="font-body text-[#8C7E6F]">{t('home.perMonth')}</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-2">
                      <div className="h-5 w-5 rounded-full bg-[#B8860B]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-3 w-3 text-[#B8860B]" />
                      </div>
                      <span className="font-body text-sm text-[#8C7E6F]">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/register" className="block">
                  <Button
                    className={`w-full rounded-xl font-body font-semibold ${
                      plan.highlighted
                        ? 'bg-[#B8860B] hover:bg-[#D4A526] text-white'
                        : 'bg-white border border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B]/5'
                    }`}
                    variant={plan.highlighted ? 'default' : 'outline'}
                  >
                    {t('home.startTrial')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center font-body text-sm text-[#8C7E6F] mt-8 space-y-1">
            <p>{t('home.trialNote')}</p>
            <p>{t('home.noCommitment')}</p>
          </div>
        </div>
      </section>

      {/* ═══════════════ CTA with radial halo ═══════════════ */}
      {!user && (
        <section className="py-24 px-4 bg-cream relative z-10 cta-halo">
          <div className="container mx-auto max-w-3xl text-center space-y-6">
            <h2 className="font-heading text-3xl md:text-4xl text-[#2B2520]">
              {t('home.ctaTitle')}
            </h2>
            <p className="font-body text-lg text-[#8C7E6F]">
              {t('home.ctaSubtitle')}
            </p>
            <div className="flex gap-4 justify-center flex-wrap pt-2">
              <Link to="/register">
                <Button className="bg-[#B8860B] hover:bg-[#D4A526] text-white rounded-xl px-8 py-3 font-body font-semibold shadow-lg">
                  {t('home.ctaCreateAccount')}
                </Button>
              </Link>
              <Link to="/providers">
                <Button variant="outline" className="border-[#B8860B]/30 text-[#B8860B] hover:bg-[#B8860B]/5 rounded-xl px-8 py-3 font-body font-semibold">
                  {t('home.ctaExplore')}
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default Home;
